import React, { useState } from 'react';
import { Card, CardContent, Stack, SxProps, Typography } from '@mui/material';
import GradeRoundedIcon from '@mui/icons-material/GradeRounded';
import Slider from 'react-slick';

import { ISubTopic } from '../../../../store/SubTopicSlice';
import { isMobileScreen } from '../../../../helper';

interface SubTopicReviewsSectionProps {
  subTopic: ISubTopic;
}

const reviewItemContainerStyles: SxProps = {
  width: '100%',
  maxWidth: '90%',
  border: '1px solid #CBD5E1',
  borderRadius: '20px',
};

const reviewItemContentContainerStyles: SxProps = {
  padding: '32px',
  background: 'transparent',
};

const sliderSettings = {
  dots: true,
  arrows: false,
  autoplay: true,
  autoplaySpeed: 4000,
  speed: 500,
};

const SubTopicReviewsSection: React.FC<SubTopicReviewsSectionProps> = ({
  subTopic,
}) => {
  const reviews = subTopic.reviews || [];
  const _sliderSettings = isMobileScreen()
    ? { ...sliderSettings, slidesToShow: 1, slidesToScroll: 1 }
    : { ...sliderSettings, slidesToShow: 3, slidesToScroll: 3 };

  // State
  const [maxHeight, setMaxHeight] = useState<number>(0);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Tutopiya Learning Platform',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: (
        reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length
      ).toFixed(1),
      reviewCount: reviews.length.toString(),
      bestRating: '5',
    },
    review: reviews.map((review) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: review.reviewer },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating.toString(),
        bestRating: '5',
      },
      reviewBody: review.review,
      datePublished: new Date().toISOString(),
    })),
  };

  return (
    <section style={{ marginTop: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <Typography
          component="h2"
          variant="h4"
          fontSize="30px"
          textAlign="center"
        >
          What our students say about us
        </Typography>
      </div>
      <div>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <Slider {..._sliderSettings}>
          {reviews.map((reviewItem, index) => {
            return (
              <Card
                key={`review-item-${index + 1}`}
                elevation={0}
                sx={reviewItemContainerStyles}
              >
                <CardContent sx={reviewItemContentContainerStyles}>
                  <div style={{ marginBottom: '15px' }}>
                    <Typography
                      fontWeight={500}
                      fontSize="14px"
                      lineHeight="16.94px"
                      color="#475569"
                      minHeight={maxHeight || '85px'}
                      textAlign="justify"
                      ref={(ref) => {
                        if (
                          ref &&
                          ref.clientHeight &&
                          maxHeight < ref.clientHeight
                        ) {
                          setMaxHeight(ref.clientHeight);
                        }

                        return ref;
                      }}
                    >
                      &quot;{reviewItem.review}&quot;
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '16.94px',
                        color: '#1E293B',
                      }}
                    >
                      {reviewItem.reviewer}
                    </Typography>
                  </div>
                  <Stack direction="row" alignItems="center">
                    {[1, 2, 3, 4, 5].map((item) => {
                      let _color = '#01736D33';

                      if (item <= reviewItem.rating) {
                        _color = '#39BFB9';
                      }

                      return (
                        <GradeRoundedIcon
                          key={`rate-item-${item}`}
                          sx={{ color: _color, width: '30px', height: '30px' }}
                        />
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Slider>
      </div>
    </section>
  );
};

export default SubTopicReviewsSection;
